import React, { useContext, useState, useCallback, useMemo } from "react";
import "./application-areas-tab-content.component.scoped.scss";
import { Box, Typography } from "@material-ui/core";
import SelectionBox from "../../create/selection-box/selection-box.component";
import { useTranslation } from "react-i18next";
import { CaseContext } from "../../../../../store";
import { AIArea } from "../../../types/AI-areas";
import { icons } from "../../../../../statics/icons";
import { Skeleton } from "@material-ui/lab";
import { Operation } from "../../../types/operations";
import TabContentTitleComponent from "../tab-content-title/tab-content-title.component";

interface SelectionItem {
  id: number;
  name: string;
  description: string;
  iconKey?: string;
}

const ApplicationAreasTabContentComponent: React.FC = () => {
  const { t } = useTranslation();
  const {
    setCaseDetailsForm,
    caseDetailsForm,
    areas,
    loading,
    operations,
  } = useContext(CaseContext);

  const [areaHover, setAreaHover] = useState<number>(-1);
  const [operationHover, setOperationHover] = useState<number>(-1);

  /**
   * Generic function to toggle selection of items (areas or operations)
   * Handles both adding and removing items from selection
   */
  const toggleSelection = useCallback(
    <T extends SelectionItem>(
      itemId: number | string,
      availableItems: T[],
      selectedIds: number[],
      selectedItems: T[],
      fieldName: "areas" | "operations",
    ) => {
      const id = Number(itemId);
      const isSelected = selectedIds.includes(id);

      if (isSelected) {
        // Remove item from selection
        const newIds = selectedIds.filter((selectedId) => selectedId !== id);
        const newItems = selectedItems.filter((item) => item.id !== id);

        setCaseDetailsForm({
          ...caseDetailsForm,
          [`${fieldName}Ids`]: newIds,
          [fieldName]: newItems,
        });
      } else {
        // Add item to selection
        const item = availableItems.find((item) => item.id === id);
        if (item) {
          setCaseDetailsForm({
            ...caseDetailsForm,
            [`${fieldName}Ids`]: [...selectedIds, id],
            [fieldName]: [...selectedItems, item],
          });
        }
      }
    },
    [caseDetailsForm, setCaseDetailsForm],
  );

  /**
   * Handle area selection toggle
   */
  const handleAreaToggle = useCallback(
    (id: number | string) => {
      toggleSelection(
        id,
        areas,
        caseDetailsForm.areasIds,
        caseDetailsForm.areas,
        "areas",
      );
    },
    [areas, toggleSelection, caseDetailsForm.areasIds, caseDetailsForm.areas],
  );

  /**
   * Handle operation selection toggle
   */
  const handleOperationToggle = useCallback(
    (id: number | string) => {
      toggleSelection(
        id,
        operations,
        caseDetailsForm.operationsIds || [],
        caseDetailsForm.operations || [],
        "operations",
      );
    },
    [
      operations,
      toggleSelection,
      caseDetailsForm.operationsIds,
      caseDetailsForm.operations,
    ],
  );

  /**
   * Check if an area is selected
   */
  const isAreaSelected = useCallback(
    (areaId: number): boolean => {
      return caseDetailsForm.areasIds.includes(areaId);
    },
    [caseDetailsForm.areasIds],
  );

  /**
   * Check if an operation is selected
   */
  const isOperationSelected = useCallback(
    (operationId: number): boolean => {
      return caseDetailsForm.operationsIds.includes(operationId);
    },
    [caseDetailsForm.operationsIds],
  );

  /**
   * Get icon for an item (area or operation)
   */
  const getItemIcon = useCallback(
    (iconKey?: string, isActive: boolean = false) => {
      if (!iconKey) {
        return isActive ? icons.categoryWhite : icons.categoryPrimary;
      }

      const iconKeyName = isActive ? `${iconKey}_WHITE` : iconKey;
      return (
        icons.PAGES.SETTINGS.ICON_OPTIONS[iconKeyName] || icons.categoryPrimary
      );
    },
    [],
  );

  /**
   * Render selection box for areas
   */
  const renderAreaBox = useCallback(
    (item: AIArea, index: number) => {
      const isSelected = isAreaSelected(item.id);
      const isHovered = areaHover === index;
      const isActive = isSelected || isHovered;

      return (
        <Box
          key={item.id}
          onMouseEnter={() => setAreaHover(index)}
          onMouseLeave={() => setAreaHover(-1)}
        >
          {loading ? (
            <Skeleton variant="text" height={60} width="100%" />
          ) : (
            <SelectionBox
              title={item.name}
              subTitle={item.description}
              // icon={getItemIcon(item.iconKey, false)}
              active={isActive}
              // activeIcon={getItemIcon(item.iconKey, true)}
              itemKey={item.id}
              onBoxClick={handleAreaToggle}
            />
          )}
        </Box>
      );
    },
    [loading, areaHover, isAreaSelected, handleAreaToggle],
  );

  /**
   * Render selection box for operations
   */
  const renderOperationBox = useCallback(
    (item: Operation, index: number) => {
      const isSelected = isOperationSelected(item.id);
      const isHovered = operationHover === index;
      const isActive = isSelected || isHovered;

      return (
        <Box
          key={item.id}
          onMouseEnter={() => setOperationHover(index)}
          onMouseLeave={() => setOperationHover(-1)}
        >
          {loading ? (
            <Skeleton variant="text" height={60} width="100%" />
          ) : (
            <SelectionBox
              title={item.name}
              subTitle={item.description}
              icon={getItemIcon(item.iconKey, false)}
              active={isActive}
              activeIcon={getItemIcon(item.iconKey, true)}
              itemKey={item.id}
              onBoxClick={handleOperationToggle}
            />
          )}
        </Box>
      );
    },
    [
      loading,
      operationHover,
      isOperationSelected,
      getItemIcon,
      handleOperationToggle,
    ],
  );

  // Memoize operationsIds to avoid undefined errors
  // eslint-disable-next-line
  const operationsIds = useMemo(() => caseDetailsForm.operationsIds || [], [
    caseDetailsForm.operationsIds,
  ]);

  return (
    <Box className="application__areas__tab__content">
      <TabContentTitleComponent
        title={t("USECASE_DETAILS.SCOPE_OF_APPLICATION_OF_THE_USE_CASE")}
        subTitle={t(
          "USECASE_DETAILS.NAME_THE_MAIN_AREAS_OF_APPLICATION_AND_LOCATIONS_OF_USE_FOR_THE_AI_PRODUCT",
        )}
        loading={loading}
      />

      <Box className="global__box__content p-0 mb-3">
        <Box className="global__box__content__header">
          {loading ? (
            <Skeleton variant="text" height={20} width={350} />
          ) : (
            <Typography
              className="text-capitalize"
              variant="h3"
              color="textPrimary"
            >
              {t(
                "USECASE_DETAILS.SELECT_THE_LOCATIONS_WHERE_THE_AI_PRODUCT_WILL_BE_USED",
              )}
            </Typography>
          )}
        </Box>
        <Box className="global__box__content__body">
          <Box className="items">
            {areas.map((item: AIArea, index: number) =>
              renderAreaBox(item, index),
            )}
          </Box>
          <span className="hint">
            {t("USECASE_CREATE.APPLICATION_AREA_HINT")}
          </span>
        </Box>
      </Box>

      <Box className="global__box__content p-0 ">
        <Box className="global__box__content__header">
          {loading ? (
            <Skeleton variant="text" height={20} width={350} />
          ) : (
            <Typography
              className="text-capitalize"
              variant="h3"
              color="textPrimary"
            >
              {t(
                "USECASE_DETAILS.SELECT_THE_APPLICATION_AREA_OF_THE_AI_PRODUCT",
              )}
            </Typography>
          )}
        </Box>
        <Box className="global__box__content__body">
          <Box className="items">
            {operations.map((item: Operation, index: number) =>
              renderOperationBox(item, index),
            )}
          </Box>
          <span className="hint mx-3">
            {t("USECASE_CREATE.OPERATION_AREAS_HINT")}
          </span>
        </Box>
      </Box>
    </Box>
  );
};

export default ApplicationAreasTabContentComponent;
